import type { ReactNode } from 'react';

/**
 * A CASCA DOS CAMPOS DO DS dentro dos cards — rótulo em cima, controle embaixo,
 * largura da coluna. Existe para os dois fatos que não podem divergir entre os
 * campos da mesma fileira:
 *
 * 1. **`.genui2` no embrulho.** Os cards do genui-kit não vivem sob a raiz de
 *    tema do DS (o web-chat monta a classe em dois pontos só, e o card não é um
 *    deles), então sem ela nenhum token do campo resolve. `bg-transparent`
 *    desfaz o `background-color: var(--background)` que o preflight escopado
 *    pinta no PRÓPRIO elemento do escopo — sem isso o campo abriria um
 *    retângulo da cor da página dentro do card.
 * 2. **A métrica do controle** (`FIELD_CONTROL_SIZE`). Numa fileira de três
 *    campos, 4px de altura a mais num deles é desalinho visível; a única forma
 *    de os campos não brigarem é a altura vir de um lugar.
 *
 * Some quando o card migrar para o DS: aí o tema é do host, e a casca é o
 * `Field`/`FieldLabel` do kit.
 */

/**
 * A caixa do controle. `h-9` é a altura do campo de busca do DS (a mesma da
 * pílula do `PlaceField`), e não o `h-8` do `Input`/`SelectTrigger`: os cards
 * legados vizinhos têm campos de ~40px, e o default do primitivo abriria 8px de
 * degrau em vez de 4. `w-full` porque a largura é da coluna do formulário.
 *
 * O `!` na altura não é decoração: o `SelectTrigger` declara a dele como
 * `data-[size=default]:h-8`, e seletor de atributo ganha de utilitária simples
 * na MESMA layer — sem o important a fileira saía com 32px num campo e 36 no
 * vizinho (medido na story `Cost Center Search`).
 */
export const FIELD_CONTROL_SIZE = 'h-9! w-full';

/** O que a casca pode carregar além do rótulo — repassado pelos campos que a usam. */
export interface FieldShellExtras {
  /** Marca visual colada ao rótulo (o ✦ do valor que veio preenchido). Decorativa:
   *  quem a usa diz o mesmo fato em texto por perto. */
  readonly adornment?: ReactNode;
  /** O que a marca diz ao leitor de tela («com sugestão da IA»): entra no nome do
   *  campo, depois do rótulo. Sem ele a marca segue muda. */
  readonly adornmentLabel?: string;
  /** Linha curta sob o controle ("Obrigatório"), ligada a ele por
   *  `aria-describedby` (`fieldMessageId`). */
  readonly message?: string;
  /** `warning` (padrão): falta uma escolha. `destructive`: o valor escolhido tem
   *  um problema que o servidor apontou — a borda do controle acompanha. Nenhum
   *  dos dois marca o campo como inválido: quem trava (ou não) o envio é o form. */
  readonly messageTone?: 'warning' | 'destructive';
  /** O controle se anuncia obrigatório (`aria-required`). Só o anúncio: quem
   *  trava o envio é o form. */
  readonly required?: boolean;
}

/** A borda do controle no tom da mensagem — só o `destructive` muda a borda. O
 *  `!` porque os controles já declaram `border-input` na mesma layer, e entre
 *  duas utilitárias de cor a ordem da folha decide. */
export function fieldToneBorder(extras: FieldShellExtras): string {
  return extras.message && extras.messageTone === 'destructive' ? ' border-destructive!' : '';
}

/** O id da linha de `message` — o controle o aponta no `aria-describedby`. */
export function fieldMessageId(fieldId: string): string {
  return `${fieldId}-message`;
}

export function FieldShell({
  label,
  fieldId,
  adornment,
  adornmentLabel,
  message,
  messageTone,
  children,
}: FieldShellExtras & {
  readonly label: string;
  /** O `for` do rótulo — o mesmo `id` que o controle carrega. */
  readonly fieldId: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="genui2 flex flex-col gap-1 bg-transparent">
      <label
        htmlFor={fieldId}
        className="flex items-center gap-1 text-sm font-medium text-foreground"
      >
        {label}
        {adornment && (
          <span aria-hidden="true" className="inline-flex">
            {adornment}
          </span>
        )}
        {adornment && adornmentLabel && <span className="sr-only"> {adornmentLabel}</span>}
      </label>
      {children}
      {message && (
        <p
          id={fieldMessageId(fieldId)}
          className={`m-0 text-xs font-medium ${
            messageTone === 'destructive' ? 'text-destructive' : 'text-warning-strong'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
